import {
  Action,
  Alert,
  Card,
  FloatingSave,
  MultiSelect,
  Page,
  SimpleSelect,
  SwitchInput,
  TextAreaInput,
  TextInput,
  UrlInput,
} from 'corso-admin-ui';
import { isTruthy, Pegasus } from 'corso-types';
import { FormEventHandler, useRef, useState } from 'react';
import { Controller, FormProvider, useFieldArray } from 'react-hook-form';
import ClipboardButton from '~/components/ClipboardButton';
import { AutomationListByEventType } from '~/features/settings/automations/AutomationListByEventType';
import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import { useCustomFields } from '~/features/settings/hooks/useCustomFields';
import { useDiscountDefinitions } from '~/features/settings/hooks/useDiscountDefinitions';
import { useFactValues } from '~/features/settings/hooks/useFactValues';
import {
  useConfigSettings,
  useConfigSettingsUpdate,
} from '~/hooks/useConfigSettings';
import {
  useRegistrationSettingsForm,
  useRegistrationSettingsFormContext,
} from '~/hooks/useRegistrationSettings';
import {
  useThemeSettings,
  useThemeSettingsUpdate,
} from '~/hooks/useThemeSettings';
import { ConfigurationAlert } from '~/features/settings/components/ConfigurationAlert';

function RegistrationProductSettings() {
  return (
    <Card>
      <AutomationListByEventType
        eventType={Pegasus.EventType.productRegistrationSelection}
        emptyText="Before registrations can be created, you must create an automation rule to determine which products are eligible for registration."
        hideIcon
        draggable
      />
    </Card>
  );
}

function RegistrationDiscountIncentiveSettings() {
  const storeDiscounts = useDiscountDefinitions();
  const hasDiscounts = storeDiscounts.data.length > 0;
  return (
    <Card>
      {hasDiscounts ? (
        <AutomationListByEventType
          eventType={Pegasus.EventType.registrationDiscountIncentive}
          hideIcon
          draggable
        />
      ) : (
        <>
          <Card.Heading>Registration Discount Incentive</Card.Heading>
          <ConfigurationAlert
            variant="info"
            heading="Discount Required"
            message="You must first create a discount to offer Registration Discount Incentives."
            to="../discounts"
          />
        </>
      )}
    </Card>
  );
}

function RegistrationTextCustomization() {
  const {
    register,
    formState: { errors },
  } = useRegistrationSettingsFormContext();

  return (
    <SettingsAnchorCard id="text-customization">
      <Card.Heading as="h2">Text Customization</Card.Heading>
      <TextInput
        id="order-lookup-registration-text"
        label="Order Lookup Start Registration Text"
        details="This text will be displayed on the order lookup page to direct customers to the registration page."
        placeholder="Didn't purchase from our online store?"
        {...register('theme.orderLookupRegistrationText')}
        error={errors.theme?.orderLookupRegistrationText?.message}
      />
      <TextInput
        id="identify-product-url"
        label="Product Identification URL"
        details="This URL will be used to direct customers to a page on your site where you can provide additional information to help them identify their product."
        placeholder="https://corso.com/locate-your-item-code"
        {...register('theme.identifyProductUrl')}
        error={errors.theme?.identifyProductUrl?.message}
      />
      <TextInput
        id="identify-product-url-button-text"
        label="Product Identification Button Text"
        details="This text will be displayed on the button that directs customers to the identify product page."
        placeholder="Find Your Product Here"
        {...register('theme.identifyProductUrlButtonText')}
        error={errors.theme?.identifyProductUrlButtonText?.message}
      />
      <TextInput
        id="identify-product-detail-text"
        label="Product Identification Detail Text"
        details="This text will be displayed alongside the identify product button, it is typically used to provide additional information or instructions to the customer."
        placeholder="Please search for the product, based on it's name, SKU or barcode number. if you need help please visit our site."
        {...register('theme.identifyProductDetailText')}
        error={errors.theme?.identifyProductDetailText?.message}
      />
      <TextInput
        id="identify-product-form-placeholder"
        label="Product Identification Search Placeholder"
        details="This text will be displayed as a placeholder in the search field on the identify product page."
        placeholder="Search by product name"
        {...register('theme.identifyProductFormPlaceholder')}
        error={errors.theme?.identifyProductFormPlaceholder?.message}
      />
      <TextInput
        id="registration-submission-text"
        label="Submission Success Text"
        details="This text will be displayed to the customer after they successfully submit a registration."
        placeholder="Thank you for your registration, please keep this number for your records."
        {...register('theme.registrationSubmissionText')}
        error={errors.theme?.identifyProductFormPlaceholder?.message}
      />
    </SettingsAnchorCard>
  );
}

export default function RegistrationSettingsPage() {
  const formId = 'registration-settings';
  const formRef = useRef<HTMLFormElement>(null);
  const isResetting = useRef(false);

  const { data: registrationSettings } = useConfigSettings(
    ({ registrations }) => registrations,
  );

  const { data: factValues } = useFactValues();

  const countryCodeOptions =
    factValues?.definite[Pegasus.Fact.orderCountry] ?? [];

  const { data: themeSettings } = useThemeSettings();
  const [showCreate, setShowCreate] = useState(false);
  const { mutateAsync: saveRegistrationSettings } = useConfigSettingsUpdate();
  const { mutateAsync: saveThemeSettings } = useThemeSettingsUpdate();
  const registrationUrl = themeSettings?.customerAppUrl
    ? `${themeSettings.customerAppUrl}/register`
    : '';

  const { data: customFields = [] } = useCustomFields();

  const methods = useRegistrationSettingsForm();
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    setValue,
    reset,
    watch,
    register,
    formState: { errors },
  } = methods;

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ theme: themeChanges, ...registrations }) =>
      Promise.all([
        saveRegistrationSettings({ registrations }),
        saveThemeSettings(themeChanges),
      ]).then(() => {
        reset();
      }),
    )(event).catch(console.error);
  };

  const isRegistrationEnabled = watch('isRegistrationEnabled');
  const registrationChannels =
    registrationSettings?.salesChannelOptions.map((c) => c.name) ?? [];
  const shouldCollectEmailMarketing = watch(
    'shouldCollectRegistrationEmailMarketingConsent',
  );
  const isCollectedAddressEnabled = watch('shouldCollectRegistrationAddress');

  const { remove, append, fields } = useFieldArray({
    control,
    name: 'customFields',
  });

  const watchedFields = watch('customFields', []);

  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchedFields[index],
  }));

  const existingFieldIds = controlledFields.map((field) => field.id);

  const customFieldSelectOptions = customFields
    .filter((cf) => !existingFieldIds.includes(cf.id))
    .map((customField) => ({
      label: customField.displayName,
      value: customField.id,
    }));

  const handleSelectCustomField = (value: string) => {
    const customField = customFields.find((cf) => `${cf.id}` === value);
    if (customField) {
      append({
        id: customField.id,
        name: customField.displayName,
        isRequired: false,
      });
    }
    formRef.current?.requestSubmit();
    setShowCreate(false);
  };

  const handleRemoveCustomField = (index: number) => {
    remove(index);
    formRef.current?.requestSubmit();
  };

  return (
    <FormProvider {...methods}>
      <Page
        title="Registrations"
        secondaryActions={[
          isRegistrationEnabled && {
            id: 'disable-returns',
            content: 'Turn Off',
            onAction: () => {
              setValue('isRegistrationEnabled', false, {
                shouldDirty: true,
              });
              formRef.current?.requestSubmit();
            },
          },
        ].filter(isTruthy)}
        primaryAction={
          isRegistrationEnabled
            ? undefined
            : {
                onAction: () => {
                  setValue('isRegistrationEnabled', true, {
                    shouldDirty: true,
                  });
                  formRef.current?.requestSubmit();
                },
                content: 'Turn On',
              }
        }
      >
        <form
          id={formId}
          ref={formRef}
          className="flex flex-col gap-5"
          onSubmit={submitHandler}
          onReset={(e) => {
            e.preventDefault();
            // reset() calls the native form.reset(), which re-fires onReset —
            // guard against the resulting infinite recursion.
            if (isResetting.current) return;
            isResetting.current = true;
            reset();
            isResetting.current = false;
          }}
        >
          {!isRegistrationEnabled && (
            <Alert
              elevated
              variant="info"
              heading="Registrations are Disabled. Settings can be configured, but registrations will not be offered to customers."
            />
          )}

          <FloatingSave
            isDirty={isDirty}
            isSubmitting={isSubmitting}
            form={formId}
          />

          <Card>
            <Card.Heading>Sales Channels</Card.Heading>
            <Controller
              name="offeredSalesChannels"
              control={control}
              defaultValue={[]}
              render={({ field: { onChange, value } }) => (
                <MultiSelect
                  labelVisuallyHidden
                  label="Channels"
                  options={registrationChannels.map((channel) => ({
                    label: channel,
                    value: channel,
                  }))}
                  value={value.map((channel) => ({
                    label: channel,
                    value: channel,
                  }))}
                  placeholder="Add a Channel"
                  onChange={(selections) =>
                    onChange(selections.map((selection) => selection.value))
                  }
                  details="Channels represent the original purchase location of a product, these channels do not correspond to Shopify sales channels."
                  creatable
                />
              )}
            />
          </Card>
          <RegistrationProductSettings />

          <RegistrationDiscountIncentiveSettings />

          <SettingsAnchorCard id="general">
            <Card.Heading>General Settings</Card.Heading>

            <UrlInput
              id="registration-url"
              label="Customer Portal Registration URL"
              details="Use this link to start a registration, customers will also be able to access the registration page from the order lookup on your Customer Portal."
              value={registrationUrl}
              required
              readOnly
              disabled
              addon={{
                outsideEnd: (
                  // using `grid` on mobile and `flex` on desktop avoid apply `grow` to each child
                  <div className="grid grid-cols-2 items-center gap-2 md:flex">
                    {/* // ? adjust to be styled as a button group  */}
                    <ClipboardButton onClick={() => registrationUrl} />
                    <Action
                      to={registrationUrl}
                      target="_blank"
                      accessibilityLabel="Visit"
                      icon="externalLink"
                    />
                  </div>
                ),
              }}
            />

            <Controller
              control={control}
              name="isRegistrationWarrantyClaimEnabled"
              defaultValue={false}
              render={({ field: { onChange, value }, fieldState }) => (
                <SwitchInput
                  id="allow-warranty-claims"
                  label="Offer Retail Warranty Claims"
                  details="Allow customers to create warranty claims from a registration."
                  checked={!!value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="useProductListPriceOnRegistration"
              defaultValue={false}
              render={({ field: { onChange, value }, fieldState }) => (
                <SwitchInput
                  id="use-product-list-price-on-registration"
                  label="Use Product List Price"
                  details="When enabled, registrations will be created with the product list price, otherwise the price will not be used."
                  checked={!!value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Card.Heading id="data-collection">Data Collection</Card.Heading>

            <Controller
              control={control}
              name="shouldCollectRegistrationProofOfPurchase"
              defaultValue={false}
              render={({ field: { onChange, value }, fieldState }) => (
                <SwitchInput
                  id="require-proof-of-purchase"
                  label="Require Proof of Purchase"
                  details="Require customers to provide proof of purchase when creating a Registration."
                  checked={!!value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="shouldCollectRegistrationEmailMarketingConsent"
              defaultValue={false}
              render={({ field: { onChange, value }, fieldState }) => (
                <SwitchInput
                  id="should-collect-email-marketing-consent"
                  label="Collect Email Marketing Consent"
                  details="When enabled, customers will be able to opt-in or out of marketing emails"
                  checked={!!value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />

            {shouldCollectEmailMarketing && (
              <>
                <UrlInput
                  required={shouldCollectEmailMarketing}
                  id="email-marketing-consent-policy-url"
                  label="Email Marketing Consent Policy URL"
                  details="The URL to the email marketing consent policy."
                  {...register('emailMarketingConsentPolicyUrl')}
                  error={errors.emailMarketingConsentPolicyUrl?.message}
                />
                <TextInput
                  id="email-marketing-consent-policy-url-text-title"
                  label="Email Marketing Consent Policy URL Title"
                  details="The title of the button that directs customers to the email marketing consent policy."
                  placeholder="View our privacy policy"
                  {...register('theme.emailMarketingConsentPolicyUrlTextTitle')}
                  required={shouldCollectEmailMarketing}
                  error={
                    errors.theme?.emailMarketingConsentPolicyUrlTextTitle
                      ?.message
                  }
                />
                <TextInput
                  required={shouldCollectEmailMarketing}
                  id="email-marketing-consent-text"
                  label="Email Marketing Consent Text"
                  defaultValue="Opt in for marketing emails"
                  details="The text that will be displayed to the customer when collecting email marketing consent."
                  {...register('theme.emailMarketingConsentText')}
                  error={errors.theme?.emailMarketingConsentText?.message}
                />

                <TextAreaInput
                  id="email-marketing-consent-description-text"
                  label="Email Marketing Consent Description Text"
                  details="The description text that will be displayed to the customer when collecting email marketing consent."
                  {...register('theme.emailMarketingConsentDescriptionText')}
                  required={shouldCollectEmailMarketing}
                  error={
                    errors.theme?.emailMarketingConsentDescriptionText?.message
                  }
                />
              </>
            )}

            <Controller
              control={control}
              name="shouldCollectRegistrationAddress"
              defaultValue={false}
              render={({ field: { onChange, value }, fieldState }) => (
                <SwitchInput
                  id="should-collect-address"
                  label="Collect Address"
                  details="Collect the customer's address when creating a registration, (address will always be collected for warranty claims created from a registration)."
                  checked={!!value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
            {isCollectedAddressEnabled && (
              <>
                <Controller
                  control={control}
                  name="theme.registrationCountryCodes"
                  render={({ field: f, fieldState }) => (
                    <MultiSelect
                      togglable
                      label="Country Options"
                      options={countryCodeOptions}
                      details="The countries that will be available for customers to select when providing their address during registration, if no countries are selected, all countries will be available."
                      value={
                        f.value
                          ?.map((v) =>
                            countryCodeOptions
                              .filter((o) => 'value' in o)
                              .find((o) => o.value === v),
                          )
                          .filter(isTruthy) ?? []
                      }
                      onChange={(o) => f.onChange(o.map((v) => v.value))}
                      error={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="theme.isRegistrationPhoneRequired"
                  defaultValue={false}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <SwitchInput
                      id="require-phone-number"
                      label="Require Address Phone Number"
                      details="Require the collected address to include a phone number."
                      checked={!!value}
                      onChange={onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </>
            )}
          </SettingsAnchorCard>

          <SettingsAnchorCard id="custom-fields">
            <Card.Line>
              <Card.Heading>Custom Fields</Card.Heading>

              <Action
                onClick={() => setShowCreate(!showCreate)}
                icon={showCreate ? 'undo' : 'add'}
                accessibilityLabel="Add custom field"
                variant="ghost"
              />
            </Card.Line>

            {showCreate ? (
              <SimpleSelect
                label="Custom Fields"
                labelVisuallyHidden
                placeholder="Select from available custom fields"
                options={customFieldSelectOptions.map(({ label, value }) => ({
                  label,
                  value: `${value}`,
                }))}
                onChange={handleSelectCustomField}
              />
            ) : (
              <div className="space-y-3">
                {controlledFields.map((customField, index) => (
                  <div
                    key={customField.id}
                    className="flex items-center justify-between gap-4"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="text-sm font-medium text-neutral-700">
                        {customField.name}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Controller
                        control={control}
                        name={`customFields.${index}.isRequired`}
                        defaultValue={false}
                        render={({
                          field: { onChange, value },
                          fieldState,
                        }) => (
                          <SwitchInput
                            id={`custom-field-${customField.id}-required`}
                            label="Required"
                            checked={value}
                            onChange={onChange}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                      <Action
                        icon="close"
                        variant="ghost"
                        accessibilityLabel="Remove custom field"
                        onClick={() => handleRemoveCustomField(index)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SettingsAnchorCard>

          <RegistrationTextCustomization />
        </form>
      </Page>
    </FormProvider>
  );
}
